/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import * as yup from 'yup';
import dayjs from 'dayjs';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import {
  listBaseChances,
  setBaseFilters,
} from '../../modules/chanceModule/chanceBases';
import { getNumberComma } from '../../utils/numberUtils';
import CstStatusChip from '../../components/common/datagrid/CstStatusChip';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
//import { ChanceListBox } from '../../components/chancemng/ChanceListBox';
import {ShareSearchBox} from '../../components/common/search/ShareSearchBox';
import StatusList from '../../components/common/info/StatusList';
import ChanceList from '../../components/chancemng/ChanceList';
import ChanceTotalSummary from '../../components/chancemng/ChanceTotalSummary';

const ChanceListContainer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { chances, defaultFilters, error, loading, totals } = useSelector(
    ({ chanceModule, loading }) => ({
      chances: chanceModule.chanceBases.chances,
      totals: chanceModule.chanceBases.totals,
      defaultFilters: chanceModule.chanceBases.defaultFilters,
      error: chanceModule.chanceBases.error,
      loading: loading['chance/LIST_BASE_CHANCES'],
    }),
    
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    const searchData = {
      useYn: 'Y',
      cstGrade: filters.searchGrade,
      prjTyp: filters.searchPrjTyp,
      prjImptsList: filters.searchPrjImpt,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listBaseChances(makeFilters(defaultFilters)));
  }, [defaultFilters]);

  useEffect(() => {
    if (chances) {
      setTableData(chances);
    }
  }, [chances]);

  const handleFilters = (name, value) => {
    const newFilters = { ...defaultFilters, [name]: value };

    dispatch(setBaseFilters(newFilters));
  };

  const handleRowClick = useCallback((param, event) => {
    navigate(`/chancemng/${param.id}/${param.row.cstGrade}/1`);
  }, []);

  // const handleAdd = () => {
  //   navigate('/chancemng/add');
  // };
  const apiRef = useGridApiRef();

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'prjSeq',
        headerName: 'No',
        minWidth: 50,
        flex: 0.5,
        type: 'number',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => params.value, 
      },
      {
        field: 'expDay',
        headerName: '매출월',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => params.value, 
      },
      {
      field: 'prjImpt',
      headerName: '중요도',
      minWidth: 70,
      flex: 0.4,
      align: 'center',
      headerAlign: 'center',
        renderCell: (params) => {
          const value = params.value;
          
          // 등급별 색상 정의 (배경색, 글자색)
          let colors = { bg: '#f5f5f5', text: '#616161' }; // 기본값 (Low/기타)

          if (value === 'High') {
            colors = { bg: '#ffebee', text: '#c62828' }; // 연한 빨강 배경 + 진한 빨강 글씨
          } else if (value === 'Medium') {
            colors = { bg: '#e8f5e9', text: '#2e7d32' }; // 연한 녹색 배경 + 진한 녹색 글씨
          } else if (value === 'Low') {
            colors = { bg: '#eceff1', text: '#546e7a' }; // 연한 회색 배경 + 진한 회색 글씨
          }

          const commonSx = {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 70,           
            height: 26,          
            borderRadius: '4px', 
            bgcolor: colors.bg,    // 연한 배경색 적용
            color: colors.text,    // 진한 글자색 적용
            fontSize: '13px',    
            fontWeight: '800',
            // 그림자는 파스텔 톤에 맞춰 조금 더 연하게 조정
            boxShadow: '0px 1px 2px rgba(0,0,0,0.05)', 
            lineHeight: 1,
            '& span': {
              display: 'block',
              marginTop: '-1px', 
            }
          };

          if (value === 'High' || value === 'Medium' || value === 'Low') {
            return (
              <Box sx={commonSx}>
                <span>{value}</span>
              </Box>
            );
          }

          return value;
        },
      },

      {
        field: 'prjTyp',
        headerName: '타입',
        minWidth: 70,
        flex: 0.4,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: ({ value }) =>
          value === 'M' ? '유지보수' : value === 'ETC' ? '기타매출' : '프로젝트',
        renderCell: (params) => {
          const value = params.value;
          let label = '프로젝트';
          let colors = { bg: '#e3f2fd', text: '#1565c0' }; // 파란색 계열

          if (value === 'M') {
            label = '유지보수';
            colors = { bg: '#e8f5e9', text: '#2e7d32' }; // 녹색 계열
          } else if (value === 'ETC') {
            label = '기타매출';
            colors = { bg: '#f3e5f5', text: '#7b1fa2' }; // 자주색 계열
          }

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 68,
                height: 24, // 높이를 살짝 줄여 더 슬림하게
                borderRadius: '4px',
                bgcolor: colors.bg,   // 연한 배경색
                color: colors.text,   // 진한 글자색
                fontSize: '12px',     // 크기를 살짝 줄여 여유 공간 확보
                fontWeight: '800',
                lineHeight: 1,
              }}
            >
              <span>{label}</span>
            </Box>
          );
        }
      },
      {
        field: 'clientCmpNm',
        headerName: '고객사명',
        minWidth: 150,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'center',
        editable: false,
      },
      {
        field: 'vendorCmpNm',
        headerName: '매출처명',
        minWidth: 150,
        flex: 1,
        type: 'string',
        align: 'left',
        headerAlign: 'center',
        editable: false,
      },

      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 300,
        flex: 1,
        align: 'left',
        headerAlign: 'center',
        renderCell: (params) => (
          <Tooltip
            title={params.value}
            arrow
            placement="top-start"
            disableInteractive={true} // [핵심] 툴팁에 마우스 오버 시 유지되는 기능 비활성화
            enterDelay={0}             // 나타날 때도 지연 없이 즉시
            leaveDelay={0}             // 사라질 때도 지연 없이 즉시
            TransitionProps={{ timeout: 0 }} // 애니메이션 제거 (딱딱 끊기게)
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#fff9c4',
                  color: '#333',
                  boxShadow: '2px 4px 12px rgba(0,0,0,0.15)',
                  border: '1px solid #fbc02d',
                  fontSize: '13px',
                  fontWeight: 500,
                  padding: '10px 12px',
                  maxWidth: 400,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  pointerEvents: 'none', // 툴팁 영역의 마우스 이벤트 완전 차단
                },
              },
              arrow: {
                sx: { color: '#fbc02d' },
              },
            }}
          >
            <Box
              sx={{
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'help',
                fontSize: '13px',
              }}
            >
              {params.value}
            </Box>
          </Tooltip>
        ),

      },

      {
        field: 'totalSales',
        headerName: '총매출액',
        minWidth: 120,
        flex: 0.6,
        description: '',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueFormatter: (params) => getNumberComma(params.value),
        renderCell: (params) => (
          <div style={{ width: '100%', textAlign: 'right' }}>
            {getNumberComma(params.value)}
          </div>
        ),
      },
      {
        field: 'totalPurchase',
        headerName: '총매입액',
        minWidth: 120,
        flex: 0.6,
        description: '',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueFormatter: (params) => getNumberComma(params.value),
        renderCell: (params) => (
          <div style={{ width: '100%', textAlign: 'right' }}>
            {getNumberComma(params.value)}
          </div>
        ),
      },
      {
        field: 'totalProfit',
        headerName: '매출이익',
        minWidth: 120,
        flex: 0.6,
        description: '',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueFormatter: (params) => getNumberComma(params.value),
        renderCell: (params) => (
          <div style={{ width: '100%', textAlign: 'right' }}>
            {getNumberComma(params.value)}
          </div>
        ),
      },
      {
        field: 'totalMargin',
        headerName: '이익률',
        width: 90, 
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          // [수정] 값이 없거나 0일 경우 0.00%로 강제 표시, 숫자 계산은 0으로 수행
          const displayValue = (params.value === undefined || params.value === null || params.value === '') 
            ? '0.00%' 
            : params.value;
            
          const rawValue = parseFloat(String(displayValue).replace('%', '')) || 0;
          const progress = Math.min(Math.max(rawValue, 0), 100);
          
          let barColor = '#eeeeee'; 

          if (rawValue < 0) {
            barColor = '#ffab91'; 
          } else if (rawValue >= 80) {
            barColor = '#1565c0'; 
          } else if (rawValue >= 60) {
            barColor = '#42a5f5'; 
          } else if (rawValue >= 40) {
            barColor = '#90caf9'; 
          } else if (rawValue >= 20) {
            barColor = '#bbdefb'; 
          } else {
            barColor = '#e0e0e0'; 
          }

          return (
            <Box sx={{ position: 'relative', width: '100%', height: 24, display: 'flex', alignItems: 'center', px: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  width: '100%',
                  height: 22, 
                  borderRadius: 1,
                  bgcolor: '#ebebeb',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: barColor,
                    borderRadius: 1,
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none'
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 900,
                    fontSize: '13px',
                    color: rawValue >= 65 || rawValue < 0 ? '#fff' : '#222',
                    textShadow: rawValue >= 65 || rawValue < 0 
                      ? '1px 1px 2px rgba(0, 0, 0, 0.5)'  
                      : '0px 0px 3px rgba(255, 255, 255, 0.8)', 
                    letterSpacing: '-0.5px'
                  }}
                >
                  {displayValue}
                </Typography>
              </Box>
            </Box>
          );
        },
      },

      {
        field: 'cstStatus',
        headerName: '상태',
        minWidth: 100,
        flex: 0.8,
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        renderCell: CstStatusChip,
      },
      {
        field: 'crtNm',
        headerName: '영업담당자',
        minWidth: 140,
        flex: 1,
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        valueGetter: (params) => `${params.row.crtNm} ${params.row.crtGradeNm}`,
        renderCell: (params) => (
          <div>
            {params.row.crtNm} {params.row.crtGradeNm}
          </div>
        ),
      },
      {
        field: 'crtDptNm',
        headerName: '부서',
        minWidth: 160,
        flex: 0.6,
        type: 'string',
        align: 'left',
        headerAlign: 'center',
        editable: false,
      },
      {
        field: 'crtDt',
        headerName: '등록일자',
        minWidth: 180,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {/*
      {
        field: 'prjNo',
        headerName: '프로젝트 번호',
        minWidth: 180,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        // renderCell: (params) => (
        //   params.row.prjNo
        // ),
      }, */}
    ],
    [],
  );

  let initialValues = {
    searchPrjImpt: defaultFilters.searchPrjImpt,
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchPrjImpt: yup.array().nullable(),
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '등록 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('등록 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        const searchData = {
          useYn: 'Y',
          searchGrade: defaultFilters.searchGrade,
          searchPrjTyp: defaultFilters.searchPrjTyp,
          searchPrjImpt: values.searchPrjImpt,
          searchKeyword: values.searchKeyword,
          searchWords: values.searchWords,
          searchStrtDt: values.searchStrtDt,
          searchEndDt: values.searchEndDt,
        };
        dispatch(setBaseFilters(searchData));

        setSubmitting(false);
      }, 1000);
    },
  });
  // const handleReset = () => {
  //   dispatch(resetBaseFilters());
  // };

  return (
    <>
      <BreadcrumbsContainer
        title="포캐스팅 관리/포캐스팅 등록 현황/포캐스팅 리스트"
        icon="chance"
      />
      <ShareSearchBox
        formik={formik}
        isLevel
      />
      <StatusList filters={defaultFilters} onFilters={handleFilters} menu={'chance'}/>
      <ChanceTotalSummary totals={totals} />
      <ChanceList
        apiRef={apiRef}
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}
        handleRowClick={handleRowClick}
        columnVisibilityModel={{
          id: false,
        }}
      />
    </>
  );
};

export default ChanceListContainer;
